import { Component, OnInit, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { take } from 'rxjs/operators';
import { showSnackbar } from 'projects/common/src/lib/components/snackbar/snackbar.component';
import { Client } from 'projects/common/src/lib/models';
import { AddressComponents } from 'projects/common/src/lib/models/address-comments.model';
import { TimeRange } from 'projects/common/src/lib/models/time-range.model';
import { Note } from '../../models/note.model';
import { LeadsService, LeadCreate } from '../../services/leads.service';
import { BusinessService } from '../../services/business.service';
import { UsersService } from '../../services/users.service';
import { ParsedLeadRow, parseCsv, parseXlsx } from './helpers/csv-parser.helper';
import { validateAllRows } from './helpers/lead-validator.helper';
import { ModalsService, ModalBehavior } from 'projects/common/src/lib/services/modals.service';
import { LeadExampleComponent } from '../../modals/lead-example/lead-example.component';

export interface ImportProgress {
  total: number;
  processed: number;
  succeeded: number;
  failed: number;
  currentRow?: number;
}

type UploadState = 'initial' | 'preview' | 'importing' | 'complete';

@Component({
  selector: 'app-lead-import',
  templateUrl: './lead-import.component.html',
  styleUrls: ['./lead-import.component.scss']
})
export class LeadImportComponent implements OnInit {
  // State management
  uploadState: UploadState = 'initial';

  // File handling
  selectedFile: File | null = null;
  fileOverDrag = false;

  // Placeholder options
  allowMissingPhone = false;
  allowMissingEmail = false;

  // Parsed data
  parsedRows: ParsedLeadRow[] = [];
  validRows: ParsedLeadRow[] = [];
  invalidRows: ParsedLeadRow[] = [];

  // Import progress
  importProgress: ImportProgress = {
    total: 0,
    processed: 0,
    succeeded: 0,
    failed: 0
  };

  // Table columns for preview
  displayedColumns: string[] = ['rowNumber', 'businessName', 'phoneNumber', 'email', 'streetAddress', 'unit', 'city', 'state', 'zip', 'jobType', 'notes', 'warnings'];

  constructor(
    private leadsService: LeadsService,
    private businessService: BusinessService,
    private usersService: UsersService,
    public router: Router,
    private snackBar: MatSnackBar,
    private modalsService: ModalsService,
    private zone: NgZone
  ) {}

  ngOnInit() {
    // Component initialization
  }

  fileUploaded(file: File): void {
    if (!file) return;

    const format = file.name?.split('.')?.pop()?.toLowerCase();
    const supportedFormats = ['csv', 'xlsx', 'xls'];

    if (!format || !supportedFormats.includes(format)) {
      showSnackbar(this.snackBar, {
        message: 'Unsupported file format. Please upload a CSV, XLS, or XLSX file.',
        duration: 3000
      });
      return;
    }

    this.selectedFile = file;
    this.parseFile(file, format);
  }

  async parseFile(file: File, format: string): Promise<void> {
    const reader: FileReader = new FileReader();
    reader.readAsBinaryString(file);

    reader.onload = async (e: any) => {
      try {
        if (format === 'csv') {
          this.parsedRows = parseCsv(e.target.result);
        } else if (format === 'xlsx' || format === 'xls') {
          this.parsedRows = parseXlsx(e.target.result);
        }

        // Validate all rows
        const { validRows, invalidRows } = validateAllRows(this.parsedRows, this.allowMissingPhone, this.allowMissingEmail);
        this.validRows = validRows;
        this.invalidRows = invalidRows;

        this.uploadState = 'preview';
      } catch (error) {
        console.error('Error parsing file:', error);
        showSnackbar(this.snackBar, {
          message: 'Error parsing file. Please check the file format and try again.',
          duration: 3000
        });
      }
    };

    reader.onerror = () => {
      showSnackbar(this.snackBar, {
        message: 'Error reading file. Please try again.',
        duration: 3000
      });
    };
  }

  async importLeads(): Promise<void> {
    this.uploadState = 'importing';
    this.importProgress = {
      total: this.validRows.length,
      processed: 0,
      succeeded: 0,
      failed: 0
    };

    const currentUser = await this.usersService.currentUser$.pipe(take(1)).toPromise();
    const business = await this.businessService.selectedBusiness$.pipe(take(1)).toPromise();

    if (!currentUser || !business) {
      showSnackbar(this.snackBar, {
        message: 'Error: User or business not found',
        duration: 3000
      });
      this.uploadState = 'preview';
      return;
    }

    const userId = currentUser.id;
    const salesTax = business.salesTax || 0;

    // Process rows sequentially
    for (let i = 0; i < this.validRows.length; i++) {
      const row = this.validRows[i];
      this.importProgress.currentRow = row.rowNumber;

      try {
        const leadData = this.transformRowToLeadCreate(row, userId, salesTax);
        await this.leadsService.createLead(leadData);
        this.importProgress.succeeded++;
      } catch (error: any) {
        console.error(`Failed to import row ${row.rowNumber}:`, error);
        row.errors.push({
          field: 'import',
          message: `Import failed: ${error?.message || 'Unknown error'}`,
          severity: 'error'
        });
        this.invalidRows.push(row);
        this.importProgress.failed++;
      }

      this.importProgress.processed++;
    }

    this.uploadState = 'complete';

    // Show success message
    showSnackbar(this.snackBar, {
      message: `Successfully imported ${this.importProgress.succeeded} leads`,
      duration: 5000
    });
  }

  private transformRowToLeadCreate(row: ParsedLeadRow, userId: number, salesTax: number): LeadCreate {
    // Build address components from parsed data
    const address: AddressComponents = {
      streetNumber: row.parsedAddress?.streetNumber || '',
      streetName: row.parsedAddress?.streetName || '',
      city: row.city || '',
      state: row.state || '',
      country: 'US',
      zip: row.zip || ''
    };

    // Use placeholder for missing phone if allowed (invalid phones will be caught by validation)
    let phoneNumber: string;
    if (!row.phoneNumber || row.phoneNumber.trim() === '') {
      phoneNumber = this.allowMissingPhone ? '0000000000' : '';
    } else {
      phoneNumber = this.cleanPhoneNumber(row.phoneNumber);
    }

    // Use placeholder for missing email if allowed
    let email: string | null = null;
    if (!row.email || row.email.trim() === '') {
      email = this.allowMissingEmail ? 'noemail@placeholder.com' : null;
    } else {
      email = row.email.trim();
    }

    // Build client
    const client: Client = {
      firstName: '',
      lastName: '',
      phoneNumber: phoneNumber,
      extNumber: null,
      address: address,
      unit: row.unit && row.unit.trim() !== '' ? row.unit : null,
      email: email,
      businessName: row.businessName,
      type: 'business',
      salesTaxPercentage: salesTax
    };

    // Build time range (2 days from now, 9 AM - 5 PM)
    const twoDaysFromNow = new Date();
    twoDaysFromNow.setDate(twoDaysFromNow.getDate() + 2);
    twoDaysFromNow.setHours(9, 0, 0, 0);

    const endTime = new Date(twoDaysFromNow);
    endTime.setHours(17, 0, 0, 0);

    const ranges: TimeRange[] = [{
      startTime: twoDaysFromNow,
      endTime: endTime
    }];

    // Build notes if provided
    const notes: Note[] = [];
    if (row.notes && row.notes.trim() !== '') {
      notes.push({
        text: row.notes,
        from: 'csv-import',
        author: userId,
        status: 'Unmark'
      });
    }

    // Build lead create object
    const leadCreate: LeadCreate = {
      inOwnerId: userId,
      inClient: client,
      inRanges: ranges,
      inAssignee: null,
      inJobType: row.jobType && row.jobType.trim() !== '' ? row.jobType : undefined,
      inNotes: notes.length > 0 ? notes : undefined
    };

    return leadCreate;
  }

  private cleanPhoneNumber(phone: string): string {
    // Remove all non-digits
    return phone.replace(/\D/g, '');
  }

  backClick(): void {
    this.router.navigate(['/lobby/schedule/leads']);
  }

  resetState(): void {
    this.selectedFile = null;
    this.parsedRows = [];
    this.validRows = [];
    this.invalidRows = [];
    this.importProgress = {
      total: 0,
      processed: 0,
      succeeded: 0,
      failed: 0
    };
  }

  cancelUpload(): void {
    this.uploadState = 'initial';
    this.resetState();
  }

  uploadAnotherFile(): void {
    this.uploadState = 'initial';
    this.resetState();
  }

  getErrorMessages(row: ParsedLeadRow): string {
    return row.errors.map(e => e.message).join(', ');
  }

  getWarningMessages(row: ParsedLeadRow): string {
    return row.warnings.map(w => w.message).join(', ');
  }

  onDragOver(): void {
    this.fileOverDrag = true;
  }

  onDragLeave(): void {
    this.fileOverDrag = false;
  }

  async openExample() {
    await this.zone.run(async () => {
      await this.modalsService.open(LeadExampleComponent, {
        behavior: ModalBehavior.Dialog,
        disableClose: true
      });
    });
  }
}
